import React, { useEffect, useState, useRef } from "react";
import HeroSection from "../components/HeroSection";
import BenefitsBanner from "../components/BenefitsBanner";
import FeaturesSection from "../components/FeaturesSection";
import BenefitsSection from "../components/BenefitsSection";
import HerbsSection from "../components/HerbsSection";
import ProcessSection from "../components/ProcessSection";
import TestimonialsSection from "../components/TestimonialsSection";
import ReviewsSection from "../components/ReviewsSection";
import CTASection from "../components/CTASection";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getUserProfileApi } from "services/authService";
import ConfirmationModal from "components/ConfirmationModal";
import SubscriptionModal from "components/SubscriptionModal";
import InfoSVG from "assets/svg/InfoSVG";
import CheckSVG from "assets/svg/CheckSVG";
import { SuccessToast } from "utils/toast";

const SUB_MODAL_SKIP_KEY = "subModalSkipCount";
const SUB_MODAL_THRESHOLD = 3;

function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, updateUser, user } = useAuth();
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const hasEvaluatedSubModalRef = useRef(false);
  const [subscriptionStatusModal, setSubscriptionStatusModal] = useState({
    show: false,
    type: null,
    title: "",
    description: "",
  });

  // Fetch user profile from /api/auth/user
  const { data: userProfileData, refetch: refetchUserProfile } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfileApi,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  // Sync profile details to global Auth state / localStorage
  useEffect(() => {
    if (userProfileData?.payload) {
      updateUser(userProfileData.payload);
    }
  }, [userProfileData]);

  useEffect(() => {
    if (location?.state?.logout) {
      SuccessToast("Logged out successfully!");
      window.history.replaceState({}, document.title, "/");
    }
  }, [location?.state]);

  // Check if returning from subscription checkout
  useEffect(() => {
    const isGoForSubscription = localStorage.getItem("isGoForSubscription");
    const payment = searchParams.get("payment");

    if (isGoForSubscription === "true" && payment) {
      const lowerPayment = payment.toLowerCase();
      if (lowerPayment === "success") {
        setSubscriptionStatusModal({
          show: true,
          type: "success",
          title: "Subscription Successful!",
          description:
            "Thank you for enrolling in the monthly subscription program. Your ongoing herbal care and formula updates are now active.",
        });
      } else if (
        lowerPayment === "decline" ||
        lowerPayment === "declined" ||
        lowerPayment === "cancel" ||
        lowerPayment === "cancelled" ||
        lowerPayment === "fail" ||
        lowerPayment === "failed"
      ) {
        setSubscriptionStatusModal({
          show: true,
          type: "decline",
          title: "Subscription Incomplete",
          description:
            "Your subscription checkout was declined or cancelled. No recurring charge has been made. You can re-enroll at any time.",
        });
      }
    }
  }, [searchParams]);

  const handleCloseSubscriptionStatusModal = async () => {
    localStorage.removeItem("isGoForSubscription");
    setSubscriptionStatusModal((prev) => ({ ...prev, show: false }));

    // Clean up query param from URL without reloading
    searchParams.delete("payment");
    setSearchParams(searchParams, { replace: true });

    // Refresh user profile details
    if (isAuthenticated) {
      try {
        const profile = await getUserProfileApi();
        if (profile?.payload) {
          updateUser(profile.payload);
        }
        if (refetchUserProfile) {
          refetchUserProfile();
        }
      } catch (error) {
        console.error("Error refreshing user profile:", error);
      }
    }
  };

  useEffect(() => {
    // Show subscription promo modal if user is authenticated, eligible, NOT already subscribed, and not in payment result modal
    const isAlreadySubscribed =
      user?.isAlreadySubscribed || user?.isSubscribed;

    if (
      isAuthenticated &&
      user?.isEligibleForSubscription &&
      !isAlreadySubscribed &&
      !subscriptionStatusModal.show &&
      !hasEvaluatedSubModalRef.current
    ) {
      hasEvaluatedSubModalRef.current = true;
      const currentSkipCount = parseInt(
        localStorage.getItem(SUB_MODAL_SKIP_KEY) || "0",
        10
      );

      if (currentSkipCount === 0 || currentSkipCount >= SUB_MODAL_THRESHOLD) {
        const timer = setTimeout(() => {
          setShowSubModal(true);
        }, 400);
        return () => clearTimeout(timer);
      } else {
        // Increment skip count for this visit
        localStorage.setItem(SUB_MODAL_SKIP_KEY, String(currentSkipCount + 1));
      }
    }
  }, [
    user?.isEligibleForSubscription,
    user?.isAlreadySubscribed,
    user?.isSubscribed,
    isAuthenticated,
    subscriptionStatusModal.show,
  ]);

  const handleCloseSubModal = () => {
    setShowSubModal(false);
    // Start counting the 3 skips after dismissal
    localStorage.setItem(SUB_MODAL_SKIP_KEY, "1");
  };

  const openQuestionnaireFlow = () => {
    confirmQuestionnaireFlow();
    // setShowQuestionnaireModal(true);
  };

  const confirmQuestionnaireFlow = () => {
    setShowQuestionnaireModal(false);
    setTimeout(() => {
      if (isAuthenticated) {
        navigate("/questionnaire");
      } else {
        window.dispatchEvent(new Event("openLoginModal"));
      }
    }, 300);
  };

  return (
    <div>
      <HeroSection onGetCured={openQuestionnaireFlow} />
      <BenefitsBanner />
      <FeaturesSection />
      <BenefitsSection onStartQuestionnaire={openQuestionnaireFlow} />
      <HerbsSection />
      <ProcessSection onStartQuestionnaire={openQuestionnaireFlow} />
      <TestimonialsSection />
      <ReviewsSection />
      <CTASection onGetHerbalPlan={openQuestionnaireFlow} />
      
      <ConfirmationModal
        show={showQuestionnaireModal}
        onCancel={() => {
          setShowQuestionnaireModal(false);
        }}
        onConfirm={confirmQuestionnaireFlow}
        title="Shipping Information"
        description="We currently operate and ship herbs only within New York. Orders from outside New York cannot be processed at this time."
        confirmText="Yes, I Agree."
        icon={<InfoSVG />}
      />

      <SubscriptionModal
        show={showSubModal}
        onHide={handleCloseSubModal}
      />

      <ConfirmationModal
        show={subscriptionStatusModal.show}
        onCancel={handleCloseSubscriptionStatusModal}
        onConfirm={handleCloseSubscriptionStatusModal}
        title={subscriptionStatusModal.title}
        description={subscriptionStatusModal.description}
        confirmText="Close"
        hideCancel={true}
        icon={
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor:
                subscriptionStatusModal.type === "success"
                  ? "#ecfdf5"
                  : "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            {subscriptionStatusModal.type === "success" ? (
              <CheckSVG size={26} color="#059669" />
            ) : (
              <InfoSVG size={26} color="#dc2626" />
            )}
          </div>
        }
      />
    </div>
  );
}

export default Home;

